import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from 'react-router-dom';
import historyStore, {selectedDevicesOf} from "../../store/historyStore";
import HistoryInputCard from "../../components/HistoryInputCard";
import HistoryNewInputCard from "../../components/HistoryNewInputCard";
import HistoryBloodInputCard from "../../components/HistoryBloodInputCard";
import HistoryWeightInputCard from "../../components/HistoryWeightInputCard";
import HistoryDeviceCard from "../../components/HistoryDeviceCard";
import HistoryDeviceValueCard from "../../components/HistoryDeviceValueCard";
import PullToRefresh from "../../components/PullToRefresh";
import { invalidateHealthDevice } from "../../utils/healthDeviceApi";
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import api from "../../utils/api";
import * as util from "../../utils/util";
import { goLoginAndReturn } from "../../utils/loginRedirect";
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {styled} from '@mui/material/styles';
import historytopimg1 from "../../assets/images/history1.png";
import historytopimg2 from "../../assets/images/history2.png";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";


// 기기 선택 카드 자리를 나타내는 키
const SELECT_CARD_KEY = '__deviceSelect__';

/**
 * AP_002_001 응답의 항목 키 → 카드 제목.
 *
 * setData 의 분기와 카드 기본 제목이 같은 곳을 보게 한다. 예전에는 제목을 setData
 * 안에서만 넣어서, 응답에 그 키가 없으면 카드 제목까지 빈 문자열로 남았다 —
 * 사용자 눈에는 카드가 통째로 안 보이는 것처럼 됐다.
 */
const CARD_TITLE = {
  meal: '오늘 섭취 칼로리',
  exercise: '오늘 소모 칼로리',
  moisture: '오늘 수분 섭취',
  step: '오늘 걸음수',
};

/**
 * 한 줄에 놓이는 카드 한 칸.
 *
 * width:100% 만 주면 flex 항목의 기본값 `min-width: auto` 때문에 **내용보다 좁아지지
 * 못한다.** 기기명이 긴 카드('CareSens N Premier')가 들어가면 그 칸만 넓어지고 옆 칸이
 * 밀려서, 기기를 무엇으로 고르느냐에 따라 카드 폭이 달라졌다.
 * flex-basis 0 + minWidth 0 으로 내용 길이와 무관하게 항상 같은 폭을 갖게 한다.
 * (칸이 좁아지면 기기명은 noWrap 으로 말줄임 처리된다)
 */
const CardSlotStyle = { flex: '1 1 0', minWidth: 0 };

const HistoryHome = () => {
  const navigate = useNavigate();
  const storeData: any = historyStore();
  const [isVisible, setIsVisible] = useState(false);
  // AP_002_001 조회 상태. 예전에는 실패해도 console.log 뿐이라 빈 카드 네 장만 남고
  // 사용자는 이유도 재시도 수단도 없었다.
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(false);
  const [healthNonce, setHealthNonce] = useState(0);
  // 혈압·혈당·체중 카드는 각자 마운트할 때 한 번만 조회한다. 이 값을 key 로 주어
  // 새로고침 때 다시 마운트시킨다(카드마다 조회 함수를 밖으로 빼지 않아도 된다).
  const [cardNonce, setCardNonce] = useState(0);


  const closePopup = () => {
    setIsVisible(false);
  };

  /**
   * 당겨서 새로고침 — 이 화면이 쓰는 세 갈래를 모두 다시 읽는다.
   *
   *  1) AP_002_001 (오늘 카드 4종) — healthNonce 로 조회 effect 재실행
   *  2) AP_009_004 (기기 측정값)   — 캐시를 비우면 떠 있는 카드가 스스로 다시 읽는다
   *  3) 혈압·혈당·체중 카드        — cardNonce 를 key 로 주어 다시 마운트
   */
  const handleRefresh = useCallback(() => {
    setHealthNonce((n) => n + 1);
    setCardNonce((n) => n + 1);
    invalidateHealthDevice();
  }, []);

  /**
   * 로고 줄의 새로고침 버튼.
   *
   * 당겨서 새로고침과 같은 동작이다(터치가 안 되는 환경·상단에 있지 않을 때를 위해).
   * 아이콘이 도는 동안 버튼을 잠가 연타로 요청이 겹치지 않게 한다.
   * 최소 회전 시간을 두는 이유는 PullToRefresh 와 같다 — 너무 빨리 끝나면 눌린 건지 알 수 없다.
   */
  const [refreshSpin, setRefreshSpin] = useState(false);
  const spinning = refreshSpin || healthLoading;

  const handleRefreshClick = () => {
    if (spinning) {
      return;
    }
    setRefreshSpin(true);
    handleRefresh();
    window.setTimeout(() => setRefreshSpin(false), 800);
  };


  useEffect(() => {
    if (util.strNull(window.localStorage.userCusID) === '') {
      setHealthLoading(false);
      return;
    }

    // 화면을 빠르게 벗어나면 응답이 늦게 도착한다 — 언마운트 뒤에는 반영하지 않는다
    let alive = true;
    setHealthLoading(true);
    setHealthError(false);

    api.get(`app/api/v1/AP_002_001`).then(res => {
      if (!alive) return;
      const resData = res.data?.result_data;
      const health = resData?.health;
      if (health === null || typeof health !== 'object') {
        console.warn('[기록] AP_002_001 응답에 health 가 없습니다:', res.data);
        setHealthError(true);
        return;
      }
      // 항목 하나가 실패해도 나머지는 그린다.
      // 예전에는 루프 전체가 한 promise 안에 있어서, 첫 항목에서 예외가 나면
      // 뒤쪽 항목은 아예 처리되지 않았다(카드 세 장이 빈 채로 남았다).
      for (let item of Object.keys(health)) {
        try {
          setData(resData, item);
        } catch (e) {
          console.warn('[기록] 항목 처리 실패:', item, e);
        }
      }
    }).catch(function (error) {
      if (!alive) return;
      console.log(error);
      setHealthError(true);
    }).finally(function () {
      if (alive) setHealthLoading(false);
    });

    // 기기 측정값(AP_009_004)은 이 화면이 직접 부르지 않는다 —
    // 카드를 그리는 HistoryDeviceValueCard 가 useHealthDevice 로 가져오고,
    // 그 훅이 날짜별 모듈 캐시를 쓰므로 카드가 여러 장이어도 요청은 한 번이다.
    //
    // 예전에는 여기서 deviceInfo.getUserData 를 한 번 더 불러 deviceInfo 안의
    // MOCK_* 배열을 응답으로 덮어쓰고 setRefresh 로 강제 리렌더했다. 그런데 그
    // MOCK_* 를 읽는 화면이 이제 하나도 없어서(전부 healthDeviceApi 로 옮겨졌다)
    // 같은 API 를 한 번 더 호출하고 화면 전체를 다시 그리기만 했다.

    return () => {
      alive = false;
    };
  }, [healthNonce]);

  /**
   * 응답에서 카드 값 하나를 꺼낸다.
   *
   * target · health_data 는 없을 수 있다. 실측(2026-08-18, dev·운영 동일) AP_002_001 은
   * `{ health, target }` 만 주고 **health_data 가 아예 없었다.** 예전에는
   * `resData.health_data[item]` 이 그 자리에서 TypeError 를 던져 루프가 통째로 끊겼고,
   * health 의 첫 키(meal)만 일부 채워진 채 나머지 세 항목은 손도 못 댔다 —
   * 화면에는 '오늘 섭취 칼로리'만 뜨고 소모 칼로리·수분 섭취·걸음수가 빈 카드로 남았다.
   */
  const valueOf = (resData: any, item: string) => ({
    target: Number(resData?.target?.[item]) || 0,
    kcal: Number(resData?.health?.[item]) || 0,
    infoText: util.strNull(resData?.health_data?.[item]),
  });

  function setData(resData: any, item: any) {
    const v = valueOf(resData, item);

    if (item === 'exercise') {
      storeData.setExerciseInfo('title', CARD_TITLE.exercise);
      storeData.setExerciseInfo('target', v.target);
      storeData.setExerciseInfo('kcal', v.kcal);
      storeData.setExerciseInfo('unitType', 'kcal');
      storeData.setExerciseInfo('infoText', v.infoText);
      storeData.setExerciseInfo('navi', () => { navigate('/history/burn', {state: {targetDateBack: '', targetDayValue: 0}}); });
    } else if (item === 'meal') {
      storeData.setMealInfo('title', CARD_TITLE.meal);
      storeData.setMealInfo('target', v.target);
      storeData.setMealInfo('kcal', v.kcal);
      storeData.setMealInfo('unitType', 'kcal');
      storeData.setMealInfo('infoText', v.infoText);
      storeData.setMealInfo('navi', () => { navigate('/history/eat', {state: {targetDateBack: '', targetDayValue: 0}}); });
    } else if (item === 'moisture') {
      storeData.setStepInfo('title', CARD_TITLE.moisture);
      storeData.setStepInfo('target', v.target);
      storeData.setStepInfo('kcal', v.kcal);
      storeData.setStepInfo('unitType', 'cup');
      storeData.setStepInfo('infoText', v.infoText);
      storeData.setStepInfo('navi', () => { navigate('/history/drink'); });
    } else if (item === 'step') {
      storeData.setMoistureInfo('title', CARD_TITLE.step);
      storeData.setMoistureInfo('target', v.target);
      storeData.setMoistureInfo('kcal', v.kcal);
      storeData.setMoistureInfo('unitType', 'step');
      storeData.setMoistureInfo('infoText', v.infoText);
      storeData.setMoistureInfo('navi', () => { navigate('/history/walking'); });
    } else {
      // 모르는 키를 조용히 넘기면 그 카드만 빈 채로 남고 원인이 화면에도 로그에도 안 남는다.
      // 서버가 항목 이름을 바꾸면 여기서 먼저 드러나야 한다.
      console.warn('[기록] AP_002_001 의 알 수 없는 항목:', item, '— 대응하는 카드가 없습니다.');
    }
  }

  const Img = styled('img')({
    margin: 'auto',
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
  });

  // 기기별 측정값 카드 + 기기 선택 카드(항상 마지막)를 2개씩 한 줄로 묶음
  // 이전 계정의 선택이 넘어오지 않도록 소유자를 확인하고 읽는다
  const selectedDeviceList: string[] = selectedDevicesOf(storeData);
  const cardItems: string[] = [...selectedDeviceList, SELECT_CARD_KEY];
  const cardRows: string[][] = [];
  for (let i = 0; i < cardItems.length; i += 2) {
    cardRows.push(cardItems.slice(i, i + 2));
  }

  // 새로고침 이미지 변경
  const ImageArr = [historytopimg1, historytopimg2,];
  const randomImg = Math.floor(Math.random() * ImageArr.length);
  const careTopImg = ImageArr[randomImg];



  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <Box sx={{ p: 0, }}>

      <Box sx={{ bgcolor: '#d5f3dd', borderRadius: '0 0 40px 40px', display: 'flex', p: 0, minWidth: 320 }} >
        {/* logo */}
        <Box position={'fixed'} width={'100%'} bgcolor={'#d5f3dd'} zIndex={999}  maxWidth={'1200px'} >
          <Stack direction={'row'} alignItems={'center'} >
            <Box>
              <ButtonBase sx={{ width: 180, height: 50, ml: 1 }}>
                <Img alt="Dual" src="/dual.png" />
              </ButtonBase>
            </Box>

            {/* 새로고침 — 당겨서 새로고침과 같은 동작 (ml:auto 로 로고 줄 오른쪽 끝) */}
            <IconButton
              aria-label="새로고침"
              onClick={handleRefreshClick}
              disabled={spinning}
              sx={{
                ml: 'auto', mr: 1, color: '#3aa92e',
                '&.Mui-disabled': { color: '#3aa92e' },
                '@keyframes historyRefreshSpin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
                '& svg': { animation: spinning ? 'historyRefreshSpin .8s linear infinite' : 'none' },
              }}
            >
              <RefreshRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <Grid container spacing={2} direction='row' sx={{ mt: 6 }}>

          <Grid item container xs sx={{ pl: 2 }} direction='column' justifyContent='center' alignContent={'center'}>
            <Typography sx={{ fontSize: '2.2rem', color: '#3aa92e', fontWeight: 900, }}>
              건강관리
            </Typography>
            <Typography sx={{ fontSize: '1.8rem', color: '#59b544', fontWeight: 500, }} gutterBottom>
              기록서비스
            </Typography>
          </Grid>
          <Grid sx={{ width: '45%', mr: 1, pt: 0, pl: 0 }} justifyContent='center' alignContent={'center'}>
            <Img alt="care" src={careTopImg} />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ p: 0, pb:12}}>

        <Box >
          <Typography sx={{ display: 'block', fontSize: '16px', color: '#444', p: '30px 0 0 0', textAlign: 'center' }}>
            <b>맞춤형 건강관리</b>를 위해<br /> <b>매일매일</b> 기록해 주세요.
          </Typography>
        </Box>

        {util.strNull(window.localStorage.userCusID) === '' ? (
          <Grid item sx={{ mt: 10 }}>
            <Box sx={BtnStyle}>
              <Button variant="contained" sx={BottomBtnStyle}
                onClick={() => goLoginAndReturn(navigate, '/history')}
              >
                로그인 후 이용해주세요.
              </Button>
            </Box>
          </Grid>
        ) : (
          <Grid item sx={{ display: 'flex', mt: 3, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', pl: 2, pr: 2 }}>
            {healthError ? (
              <Stack alignItems={'center'} sx={{ width: '100%', mb: '20px' }}>
                <Typography sx={{ fontSize: 14, color: '#888' }}>오늘 기록을 불러오지 못했습니다.</Typography>
                <Button onClick={() => setHealthNonce((n) => n + 1)} sx={{ color: '#EF8322', fontSize: 14, fontWeight: 700 }}>
                  다시 시도
                </Button>
              </Stack>
            ) : null}

            <Stack direction={'row'} spacing={2} sx={{ display: 'flex', mb: '20px', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <Box sx={CardSlotStyle}>
                <HistoryInputCard healthInfo={storeData.mealInfo} title={CARD_TITLE.meal} loading={healthLoading} />
              </Box>
              <Box sx={{
                ...CardSlotStyle,
                '& .MuiGauge-valueArc':{fill:'#ff5454'},
                '& .MuiLinearProgress-bar': {bgcolor:'#ff5454'}
                }}>
                <HistoryInputCard healthInfo={storeData.exerciseInfo} title={CARD_TITLE.exercise} loading={healthLoading} />
              </Box>
            </Stack>
            <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center', mb: '20px',justifyContent: 'center', width: '100%' }}>
              <Box sx={{
                ...CardSlotStyle,
                '& .MuiGauge-valueArc':{fill:'#33cccc'},
                '& .MuiLinearProgress-bar': {bgcolor:'#33cccc'}
                }}>
                <HistoryInputCard healthInfo={storeData.stepInfo} title={CARD_TITLE.moisture} loading={healthLoading} />
              </Box>
              <Box sx={{
                ...CardSlotStyle,
                '& .MuiGauge-valueArc':{fill:'#e029ff'},
                '& .MuiLinearProgress-bar': {bgcolor:'#e029ff'}
                }}>
                <HistoryInputCard healthInfo={storeData.moistureInfo} title={CARD_TITLE.step} loading={healthLoading} />
              </Box>
            </Stack>


            <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center',  mb: '20px',justifyContent: 'center', width: '100%' }}>
              <Box sx={CardSlotStyle}>
                {/* key — 당겨서 새로고침 시 다시 마운트해 재조회한다 */}
                <HistoryNewInputCard key={cardNonce} />
              </Box>
              <Box sx={CardSlotStyle}>
                <HistoryBloodInputCard key={cardNonce} />
              </Box>
            </Stack>

            {/* 오늘 체중 기록 - 가로 전체 폭 */}
            <Stack direction={'row'} spacing={2} sx={{ alignItems: 'stretch', mb: '20px', justifyContent: 'center', width: '100%' }}>
              <Box sx={CardSlotStyle}>
                <HistoryWeightInputCard key={cardNonce} />
              </Box>
            </Stack>

            {/* 선택한 기기별 측정값 + 기기 선택 (마지막 빈칸에 배치) */}
            {cardRows.map((cardRow, rowIndex) => (
              <Stack key={rowIndex} direction={'row'} spacing={2}
                sx={{ alignItems: 'stretch', mb: rowIndex === cardRows.length - 1 ? 0 : '20px', justifyContent: 'center', width: '100%' }}>
                {cardRow.map((cardKey) => (
                  <Box key={cardKey} sx={CardSlotStyle}>
                    {cardKey === SELECT_CARD_KEY ? (
                      <HistoryDeviceCard />
                    ) : (
                      <HistoryDeviceValueCard deviceId={cardKey} />
                    )}
                  </Box>
                ))}
                {cardRow.length === 1 ? <Box sx={CardSlotStyle} /> : null}
              </Stack>
            ))}
          </Grid>
        )}

      </Box>

      {/* <Dialog open={isVisible} onClose={closePopup}>
        <DialogTitle>안내</DialogTitle>
        <DialogContent>
          <Typography component="p">현재 정부 전산망 장애로 인해 서비스 일부가 제한되고 있습니다.</Typography>
          <Typography component="p">복구 작업이 완료되는 대로 즉시 정상화될 예정입니다.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePopup} color="primary">
            닫기
          </Button>
        </DialogActions>
      </Dialog> */}
    </Box>
    </PullToRefresh>
  );
}


const BtnStyle = {
  display: 'flex',
  justifyContent: 'center',
  m: 3,
};

const BottomBtnStyle = {
  width: '100%',
  bgcolor: '#EF8322',
  fontSize: 18,
  fontWeight: 700,
  borderRadius: '30px', p: 1,
  boxSizing: 'border-box',
  color: '#fff',
  border: '1px solid #ef8322',
  ':hover': {
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
    boxShadow: 'none',
    color: '#fff'
  },
  ':active': {
    boxShadow: 'none',
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
  },
  ':focus': {
    boxShadow: 'none',
  },
};


export default HistoryHome;